/**
 * RulesAdminPage — admin surface for the Rules Judge corpus + the
 * pending-admin queue (C10) plus a cost panel (C11).
 *
 * Three tabs:
 *   - Clarifications: list active rows; supersede with a new resolution.
 *   - Pending Rulings: queue of NO_AUTHORITATIVE_ANSWER rulings;
 *     close one by selecting / pasting the canonical clarification id.
 *   - Cost: SUM(cost_usd) over a 7d window with per-day + per-model
 *     breakdowns. Authoritative spend lives on the AI Gateway dashboard;
 *     this is the in-app slice that ties spend to escalation rates.
 *
 * Mirrors the structure of NeedsReviewPage.tsx — same Tailwind cosmos
 * palette, same fetch + setState pattern, no router.
 */

import { type ReactElement, useCallback, useEffect, useState } from 'react';
import { apiPath } from '../api.js';

type Clarification = {
  clarification_id: number;
  source: string;
  topic: string;
  scenario: string;
  resolution: string;
  cites_skill_refs: string | null;
  authored_by: string;
  authored_at: number;
  authority_score: number;
  active: number;
};

type PendingRow = {
  pending_id: number;
  ruling_id: number;
  question: string;
  context_json: string;
  enqueued_at: number;
  asker: string | null;
  match_ulid: string | null;
  confidence: string | null;
};

type CostSummary = {
  window: { days: number; since: number };
  totals: { rulings: number; cost_usd: number; escalated: number; pending_human: number };
  perDay: ReadonlyArray<{ day: string; rulings: number; cost: number }>;
  perModel: ReadonlyArray<{ model: string; rulings: number; cost: number }>;
};

type Tab = 'clarifications' | 'pending' | 'cost';

export function RulesAdminPage(): ReactElement {
  const [tab, setTab] = useState<Tab>('clarifications');

  return (
    <div className="mx-auto max-w-5xl p-6 text-cosmos-100">
      <header className="mb-4 flex items-baseline justify-between">
        <h1 className="font-bold text-2xl text-amber-200">Rules Judge</h1>
        <nav className="flex gap-2 text-sm">
          {(['clarifications', 'pending', 'cost'] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`rounded px-3 py-1 ${
                tab === t ? 'bg-amber-600 text-cosmos-950' : 'bg-cosmos-800 hover:bg-cosmos-700'
              }`}
            >
              {t === 'clarifications' ? 'Clarifications' : t === 'pending' ? 'Pending' : 'Cost'}
            </button>
          ))}
        </nav>
      </header>

      {tab === 'clarifications' ? <ClarificationsTab /> : null}
      {tab === 'pending' ? <PendingTab /> : null}
      {tab === 'cost' ? <CostTab /> : null}
    </div>
  );
}

function ClarificationsTab(): ReactElement {
  const [rows, setRows] = useState<readonly Clarification[]>([]);
  const [topic, setTopic] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editText, setEditText] = useState<string>('');
  const [status, setStatus] = useState<string | null>(null);

  // New-row form state.
  const [newTopic, setNewTopic] = useState<string>('');
  const [newScenario, setNewScenario] = useState<string>('');
  const [newResolution, setNewResolution] = useState<string>('');
  const [newRefs, setNewRefs] = useState<string>('');

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = topic
        ? apiPath(`/api/admin/clarifications?topic=${encodeURIComponent(topic)}`)
        : apiPath('/api/admin/clarifications');
      const res = await fetch(url, { credentials: 'include' });
      const body = (await res.json()) as {
        ok: boolean;
        clarifications?: readonly Clarification[];
        error?: string;
      };
      if (body.ok && body.clarifications) {
        setRows(body.clarifications);
      } else {
        setError(body.error ?? 'unknown error');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [topic]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const supersede = useCallback(
    async (id: number, resolution: string) => {
      try {
        const res = await fetch(apiPath(`/api/admin/clarifications/${id}/supersede`), {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ resolution }),
        });
        const body = (await res.json()) as { ok: boolean; error?: string };
        if (body.ok) {
          setStatus(`Superseded #${id}`);
          setEditingId(null);
          setEditText('');
          await reload();
        } else {
          setStatus(`Error: ${body.error ?? 'unknown'}`);
        }
      } catch (e) {
        setStatus(`Error: ${e instanceof Error ? e.message : String(e)}`);
      }
    },
    [reload],
  );

  const create = useCallback(async () => {
    if (!newTopic || !newScenario || !newResolution) {
      setStatus('topic, scenario, resolution all required');
      return;
    }
    try {
      const res = await fetch(apiPath('/api/admin/clarifications'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          topic: newTopic,
          scenario: newScenario,
          resolution: newResolution,
          cites_skill_refs: newRefs || undefined,
        }),
      });
      const body = (await res.json()) as {
        ok: boolean;
        authority?: number;
        error?: string;
      };
      if (body.ok) {
        setStatus(`Inserted (authority ${body.authority ?? '?'})`);
        setNewTopic('');
        setNewScenario('');
        setNewResolution('');
        setNewRefs('');
        await reload();
      } else {
        setStatus(`Error: ${body.error ?? 'unknown'}`);
      }
    } catch (e) {
      setStatus(`Error: ${e instanceof Error ? e.message : String(e)}`);
    }
  }, [newTopic, newScenario, newResolution, newRefs, reload]);

  return (
    <div>
      <div className="mb-4 flex items-center gap-2">
        <input
          type="text"
          placeholder="Filter by topic substring…"
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          className="flex-1 rounded border border-cosmos-700 bg-cosmos-950 px-2 py-1 text-cosmos-100 text-sm focus:border-amber-500 focus:outline-none"
        />
        <button
          type="button"
          onClick={() => void reload()}
          className="rounded bg-cosmos-800 px-3 py-1 text-sm hover:bg-cosmos-700"
        >
          Reload
        </button>
      </div>

      <details className="mb-4 rounded border border-cosmos-800 bg-cosmos-900/60 p-3">
        <summary className="cursor-pointer font-semibold text-amber-300 text-sm">
          + New clarification
        </summary>
        <div className="mt-2 grid grid-cols-1 gap-2">
          <input
            placeholder="topic (snake_case label)"
            value={newTopic}
            onChange={(e) => setNewTopic(e.target.value)}
            className="rounded border border-cosmos-700 bg-cosmos-950 px-2 py-1 text-sm"
          />
          <textarea
            placeholder="scenario"
            value={newScenario}
            onChange={(e) => setNewScenario(e.target.value)}
            rows={3}
            className="rounded border border-cosmos-700 bg-cosmos-950 px-2 py-1 text-sm"
          />
          <textarea
            placeholder="resolution"
            value={newResolution}
            onChange={(e) => setNewResolution(e.target.value)}
            rows={3}
            className="rounded border border-cosmos-700 bg-cosmos-950 px-2 py-1 text-sm"
          />
          <input
            placeholder="cites_skill_refs (e.g. §5.4,§7)"
            value={newRefs}
            onChange={(e) => setNewRefs(e.target.value)}
            className="rounded border border-cosmos-700 bg-cosmos-950 px-2 py-1 text-sm"
          />
          <button
            type="button"
            onClick={() => void create()}
            className="self-start rounded bg-emerald-600 px-3 py-1 font-semibold text-cosmos-950 text-sm hover:bg-emerald-500"
          >
            Insert
          </button>
        </div>
      </details>

      {status ? <p className="mb-2 text-amber-300 text-sm">{status}</p> : null}
      {error ? (
        <div className="mb-4 rounded border border-rose-700 bg-rose-950/40 px-3 py-2 text-rose-200 text-sm">
          {error}
        </div>
      ) : null}

      {loading ? (
        <p className="text-cosmos-400">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-cosmos-400">No active clarifications.</p>
      ) : (
        <ul className="space-y-2">
          {rows.map((r) => (
            <li
              key={r.clarification_id}
              className="rounded border border-cosmos-800 bg-cosmos-900/60 p-3"
            >
              <div className="mb-1 flex items-baseline justify-between">
                <span className="font-mono text-amber-300">
                  #{r.clarification_id} · {r.topic}
                </span>
                <span className="text-cosmos-500 text-xs">
                  authority {r.authority_score} · {r.source}
                </span>
              </div>
              <p className="mb-1 text-cosmos-200 text-sm">
                <strong className="text-cosmos-300">scenario:</strong> {r.scenario}
              </p>
              <p className="mb-1 text-cosmos-200 text-sm">
                <strong className="text-cosmos-300">resolution:</strong> {r.resolution}
              </p>
              {r.cites_skill_refs ? (
                <p className="mb-1 text-cosmos-400 text-xs">
                  ties to: <span className="font-mono">{r.cites_skill_refs}</span>
                </p>
              ) : null}
              {editingId === r.clarification_id ? (
                <div className="mt-2 flex gap-2">
                  <input
                    placeholder="New resolution"
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    className="flex-1 rounded border border-cosmos-700 bg-cosmos-950 px-2 py-1 text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => void supersede(r.clarification_id, editText)}
                    className="rounded bg-amber-600 px-3 py-1 font-semibold text-cosmos-950 text-sm"
                  >
                    Supersede
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingId(null);
                      setEditText('');
                    }}
                    className="rounded bg-cosmos-700 px-3 py-1 text-sm"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setEditingId(r.clarification_id);
                    setEditText(r.resolution);
                  }}
                  className="mt-1 rounded bg-cosmos-700 px-2 py-1 text-cosmos-100 text-xs hover:bg-cosmos-600"
                >
                  Edit / supersede
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PendingTab(): ReactElement {
  const [rows, setRows] = useState<readonly PendingRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [resolveBy, setResolveBy] = useState<Record<number, string>>({});
  const [status, setStatus] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(apiPath('/api/admin/judge/pending'), { credentials: 'include' });
      const body = (await res.json()) as {
        ok: boolean;
        pending?: readonly PendingRow[];
        error?: string;
      };
      if (body.ok && body.pending) {
        setRows(body.pending);
      } else {
        setError(body.error ?? 'unknown error');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const resolve = useCallback(
    async (pendingId: number, clarificationId: number) => {
      try {
        const res = await fetch(apiPath(`/api/admin/judge/pending/${pendingId}/resolve`), {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ clarification_id: clarificationId }),
        });
        const body = (await res.json()) as { ok: boolean; error?: string };
        if (body.ok) {
          setStatus(`Resolved #${pendingId} → clarification #${clarificationId}`);
          await reload();
        } else {
          setStatus(`Error: ${body.error ?? 'unknown'}`);
        }
      } catch (e) {
        setStatus(`Error: ${e instanceof Error ? e.message : String(e)}`);
      }
    },
    [reload],
  );

  return (
    <div>
      {status ? <p className="mb-2 text-amber-300 text-sm">{status}</p> : null}
      {error ? (
        <div className="mb-4 rounded border border-rose-700 bg-rose-950/40 px-3 py-2 text-rose-200 text-sm">
          {error}
        </div>
      ) : null}
      {loading ? (
        <p className="text-cosmos-400">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-cosmos-400">Pending queue empty.</p>
      ) : (
        <ul className="space-y-2">
          {rows.map((r) => {
            const idStr = resolveBy[r.pending_id] ?? '';
            return (
              <li
                key={r.pending_id}
                className="rounded border border-cosmos-800 bg-cosmos-900/60 p-3"
              >
                <div className="mb-1 flex items-baseline justify-between">
                  <span className="font-mono text-amber-300">#{r.pending_id}</span>
                  <span className="text-cosmos-500 text-xs">
                    {r.asker ?? 'unknown'} · {r.match_ulid ?? 'no match'} ·{' '}
                    {new Date(r.enqueued_at).toISOString()}
                  </span>
                </div>
                <p className="mb-2 text-cosmos-200 text-sm">{r.question}</p>
                <div className="flex gap-2">
                  <input
                    placeholder="clarification_id to attach"
                    value={idStr}
                    onChange={(e) =>
                      setResolveBy((s) => ({ ...s, [r.pending_id]: e.target.value }))
                    }
                    className="flex-1 rounded border border-cosmos-700 bg-cosmos-950 px-2 py-1 text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const id = Number.parseInt(idStr, 10);
                      if (Number.isFinite(id)) void resolve(r.pending_id, id);
                    }}
                    className="rounded bg-emerald-600 px-3 py-1 font-semibold text-cosmos-950 text-sm"
                  >
                    Resolve
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function CostTab(): ReactElement {
  const [data, setData] = useState<CostSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [days, setDays] = useState<number>(7);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(apiPath(`/api/admin/judge/cost?days=${days}`), {
        credentials: 'include',
      });
      const body = (await res.json()) as { ok: boolean; error?: string } & CostSummary;
      if (body.ok) {
        setData(body);
      } else {
        setError(body.error ?? 'unknown error');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    void reload();
  }, [reload]);

  if (loading) return <p className="text-cosmos-400">Loading…</p>;
  if (error)
    return (
      <div className="rounded border border-rose-700 bg-rose-950/40 px-3 py-2 text-rose-200 text-sm">
        {error}
      </div>
    );
  if (!data) return <p className="text-cosmos-400">No data.</p>;

  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <label className="text-cosmos-400 text-sm" htmlFor="judge-cost-days">
          Window (days):
        </label>
        <input
          id="judge-cost-days"
          type="number"
          min={1}
          max={90}
          value={days}
          onChange={(e) => setDays(Number(e.target.value) || 7)}
          className="w-20 rounded border border-cosmos-700 bg-cosmos-950 px-2 py-1 text-sm"
        />
      </div>

      <section className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Card label="Rulings" value={data.totals.rulings.toString()} />
        <Card label="Cost (USD)" value={`$${data.totals.cost_usd.toFixed(4)}`} />
        <Card label="Escalated" value={data.totals.escalated.toString()} />
        <Card label="Pending human" value={data.totals.pending_human.toString()} />
      </section>

      <h2 className="mb-2 font-semibold text-amber-200 text-sm">Per day</h2>
      <table className="mb-4 w-full text-cosmos-200 text-sm">
        <thead>
          <tr className="text-cosmos-400">
            <th className="text-left">Day</th>
            <th className="text-right">Rulings</th>
            <th className="text-right">Cost (USD)</th>
          </tr>
        </thead>
        <tbody>
          {data.perDay.map((d) => (
            <tr key={d.day} className="border-cosmos-800 border-t">
              <td className="py-1 font-mono">{d.day}</td>
              <td className="py-1 text-right">{d.rulings}</td>
              <td className="py-1 text-right">${d.cost.toFixed(4)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h2 className="mb-2 font-semibold text-amber-200 text-sm">Per model</h2>
      <table className="w-full text-cosmos-200 text-sm">
        <thead>
          <tr className="text-cosmos-400">
            <th className="text-left">Model</th>
            <th className="text-right">Rulings</th>
            <th className="text-right">Cost (USD)</th>
          </tr>
        </thead>
        <tbody>
          {data.perModel.map((m) => (
            <tr key={m.model} className="border-cosmos-800 border-t">
              <td className="py-1 font-mono">{m.model}</td>
              <td className="py-1 text-right">{m.rulings}</td>
              <td className="py-1 text-right">${m.cost.toFixed(4)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Card({ label, value }: { label: string; value: string }): ReactElement {
  return (
    <div className="rounded border border-cosmos-800 bg-cosmos-900/60 p-3">
      <p className="text-cosmos-400 text-xs uppercase tracking-wide">{label}</p>
      <p className="font-bold text-amber-300 text-xl">{value}</p>
    </div>
  );
}
